import { useCallback, type FC, type FormEvent, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AuthRoute, Logo } from '../components';
import { useAuthContext } from '../hooks';
import { AuthApi } from '../api';
import { toast } from 'react-toastify';

interface LoginFormFields {
  username: string;
  password: string;
}

interface OtpFormFields {
  otp: string;
}

export const LoginPage: FC = () => {
  const { signIn, twoFactorAuth } = useAuthContext();
  const [is2faRequired, setIs2faRequired] = useState(false);
  const [otpToken, setOtpToken] = useState<string | null>(null);
  /** Снимаем readOnly после фокуса — иначе Chrome подставляет логин в «единственное» текстовое поле */
  const [otpFieldUnlocked, setOtpFieldUnlocked] = useState(false);

  useEffect(() => {
    if (!is2faRequired) {
      setOtpFieldUnlocked(false);
    }
  }, [is2faRequired]);
  const loginForm = useForm<LoginFormFields>();
  const otpForm = useForm<OtpFormFields>();

  const onLoginSubmit: SubmitHandler<LoginFormFields> = useCallback(async (data) => {
    try {
      const response = await AuthApi.logIn(data);
      
      if (response.is2faEnabled && response.otpToken) {
        // Требуется 2FA - показываем форму OTP
        // Куки не установлены, нужно будет отправить OTP
        setIs2faRequired(true);
        setOtpToken(response.otpToken);
      } else {
        // Обычная авторизация прошла успешно
        // Куки установлены, авторизуем пользователя
        signIn(data);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        toast.error(error.response.statusText || 'Invalid credentials');
      } else {
        toast.error('Login failed');
      }
    }
  }, [signIn]);

  const onOtpSubmit: SubmitHandler<OtpFormFields> = useCallback((data) => {
    if (otpToken) {
      twoFactorAuth({
        otp: data.otp,
        otpToken: otpToken,
      });
    }
  }, [otpToken, twoFactorAuth]);

  const handleBackToLogin = useCallback(() => {
    setIs2faRequired(false);
    setOtpToken(null);
    otpForm.reset();
  }, [otpForm]);

  const handleOtpInput = useCallback((event: FormEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    const digitsOnly = target.value.replace(/\D/g, '');
    if (target.value !== digitsOnly) {
      target.value = digitsOnly;
    }
  }, []);

  return (
    <AuthRoute>
      <div className="flex-center min-h-screen bg-auth">
        <div className="w-[520px] bg-white px-8 py-10 rounded-3xl">
          <h1 className="mb-3 flex-center">
            <Logo className="text-4xl" />
          </h1>
          
          {!is2faRequired ? (
            <>
              <h2 className="mb-9 mx-auto w-[300px] text-sm text-brand-2 text-center">
                Log in to your personal account using your username and password
              </h2>
              <form
                className="grid gap-8"
                onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                autoComplete="on"
              >
                <input
                  {...loginForm.register('username', {
                    required: true,
                  })}
                  className="input"
                  placeholder="Login"
                  type="text"
                  autoComplete="username"
                />
                <input
                  {...loginForm.register('password', {
                    required: true,
                  })}
                  autoComplete="current-password"
                  className="input"
                  placeholder="Password"
                  type="password"
                />
                <button className="button button--block button--primary" type="submit">
                  Log In
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="mb-9 mx-auto w-[300px] text-sm text-brand-2 text-center">
                Enter your one-time password (OTP)
              </h2>
              <form
                className="grid gap-8"
                onSubmit={otpForm.handleSubmit(onOtpSubmit)}
                autoComplete="off"
              >
                <input
                  tabIndex={-1}
                  aria-hidden
                  className="hidden"
                  autoComplete="username"
                  name="fake_username"
                  type="text"
                />
                <input
                  tabIndex={-1}
                  aria-hidden
                  className="hidden"
                  autoComplete="current-password"
                  name="fake_password"
                  type="password"
                />
                <input
                  {...otpForm.register('otp', {
                    required: true,
                    pattern: /^\d+$/,
                  })}
                  className="input"
                  placeholder="OTP Code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  readOnly={!otpFieldUnlocked}
                  onFocus={() => setOtpFieldUnlocked(true)}
                  onInput={handleOtpInput}
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                />
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    className="button button--block button--secondary" 
                    type="button"
                    onClick={handleBackToLogin}
                  >
                    Back
                  </button>
                  <button className="button button--block button--primary" type="submit">
                    Verify
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </AuthRoute>
  );
};
